import { each, isEmpty, isNil, pick } from 'lodash';
import { LocalTokenStore } from './LocalTokenStore';
import { ITokens, ITokenStore, TokenTypes } from './TokenStore';

export interface ICookies {
  set(key: string, value: string, options?: CookieOptions): string;
  get(key: string): string;
  remove(key: string): void;
}


export interface CookieOpts {
  readonly name?: string;
  [key:string]: any;
}

export interface CookieOptions {
  // JSII doesn't like this but this is the way it should be
  // [key in TokenTypes]?: CookieOpts;
  readonly id?: CookieOpts;
  readonly refresh?: CookieOpts;
  readonly access?: CookieOpts;
  readonly restricted?: CookieOpts;
}

/**
 * TODO explain that you shouldn't store id, refresh, and access tokens together in cookies
 *
 *
 * for now only store refreshToken in cookies
 */
export class CookieTokenStore implements ITokenStore {
  environment?: string;

  /**
   *
   * @param { cookies } assume an instance of js-cookie
   */
  constructor(
    private _cookies: ICookies,
    private _cookieOptions: CookieOptions = {
      refresh: {},
    },
    private _delegate: ITokenStore = new LocalTokenStore(),
  ) {}

  get expiresIn(): string | undefined {
    throw new Error('Method not implemented.');
  }

  private setTokens(tokens: ITokens) {
    const { _cookieOptions } = this;

    each(tokens, (token, name) => {
      if (_cookieOptions[name as TokenTypes]) {
        this.setToken(name as TokenTypes, token);
      }
    });
  }

  updateTokens(tokens: ITokens) {
    this.setTokens(tokens);
    this._delegate.updateTokens(tokens);
  }

  async clear(tokensToClear?: TokenTypes[]): Promise<any> {
    tokensToClear = tokensToClear || [TokenTypes.ID, TokenTypes.REFRESH, TokenTypes.ACCESS];

    const tokens = pick({
      [TokenTypes.ID]: undefined,
      [TokenTypes.REFRESH]: undefined,
      [TokenTypes.ACCESS]: undefined,
    }, tokensToClear);

    this.setTokens(tokens);
    return this._delegate.clear(tokensToClear);
  }

  get access(): string | undefined {
    return this.getToken(TokenTypes.ACCESS);
  }
  get id() { return this.getToken(TokenTypes.ID); }
  get refresh() { return this.getToken(TokenTypes.REFRESH); }

  set access(value: string | undefined) { this.setToken(TokenTypes.ACCESS, value); }
  set id(value: string | undefined) { this.setToken(TokenTypes.ID, value); }
  set refresh(value: string | undefined) { this.setToken(TokenTypes.REFRESH, value); }

  cookieName(name: TokenTypes): string {
    const { name: cookieName } = this._cookieOptions[name] as CookieOpts;
    const prefix = this.environment ? `${this.environment}-` : '';
    return `${prefix}${cookieName || name}`;
  }

  getToken(name: TokenTypes): string | undefined {
    const { _delegate, _cookieOptions, _cookies } = this;
    if (_cookieOptions[name]) {
      const value = _cookies.get(this.cookieName(name));
      return isEmpty(value) ? undefined : value;
    } else {
      return _delegate[name];
    }
  }

  setToken(name: TokenTypes, value: string | number | undefined) {
    this.setCookie(name, isNil(value) ? value : `${value}`);
  }

  setCookie(name: TokenTypes, value?: string) {
    const { _cookieOptions, _cookies } = this;
    if (!_cookieOptions[name]) {throw `not allowed to set ${name} token in cookie`;}

    const { name: cookieName, ...options } = _cookieOptions[name] as CookieOpts;
    if (isNil(value)) {
      _cookies.remove(this.cookieName(name));

    } else {
      _cookies.set(this.cookieName(name), value, options);

    }
  }

}

export default CookieTokenStore;
